import { useCallback, useEffect, useMemo, useState } from 'react'

type PaginationResetKey = string | number | boolean | null | undefined

type UseClientPaginationResult<T> = {
  currentPage: number
  totalPage: number
  pageItems: T[]
  goPrev: () => void
  goNext: () => void
}

export function useClientPagination<T>(
  items: T[],
  pageSize: number,
  resetKey?: PaginationResetKey,
): UseClientPaginationResult<T> {
  const [pagination, setPagination] = useState({
    page: 1,
    resetKey,
  })
  const page =
    Object.is(pagination.resetKey, resetKey) ? pagination.page : 1
  const totalPage =
    items.length === 0 ? 0 : Math.ceil(items.length / pageSize)
  const currentPage = totalPage > 0 ? Math.min(page, totalPage) : 1

  useEffect(() => {
    if (!Object.is(pagination.resetKey, resetKey)) {
      setPagination({ page: 1, resetKey })
      return
    }

    if (totalPage === 0 && page !== 1) {
      setPagination({ page: 1, resetKey })
      return
    }

    if (totalPage > 0 && page > totalPage) {
      setPagination({ page: totalPage, resetKey })
    }
  }, [page, pagination.resetKey, resetKey, totalPage])

  const pageItems = useMemo(() => {
    if (items.length === 0) {
      return []
    }

    const start = (currentPage - 1) * pageSize
    return items.slice(start, start + pageSize)
  }, [currentPage, items, pageSize])

  const goPrev = useCallback(() => {
    setPagination({ page: Math.max(1, currentPage - 1), resetKey })
  }, [currentPage, resetKey])

  const goNext = useCallback(() => {
    setPagination({
      page: Math.min(totalPage || 1, currentPage + 1),
      resetKey,
    })
  }, [currentPage, resetKey, totalPage])

  return {
    currentPage,
    totalPage,
    pageItems,
    goPrev,
    goNext,
  }
}
